import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSearch, type Candidate, type SearchAttempt, type SearchResource } from "../api/searches";
import { progressFrom } from "../api/searchProgress";
import { DEFAULT_WAIT_TUNING, type WaitTuning } from "./waitTuning";

// The wait/release machine: a universal minimum confidence wait (even a search
// we already know will release must first read as work), then signal-driven
// patience — a genuinely running search holds up to the cap with live
// feedback, a backlogged or offline one releases at the minimum wait. The
// backlog/offline distinction never reaches the user: one release surface,
// whatever the reason. Exits on whichever comes first — a presentable match,
// the search finishing empty, or the release.
//
// The user-facing wait is not the search budget: sources keep running
// server-side after release, so polling continues (slower) while mounted and
// a match still upgrades the surface in place.
//
// The clocks start when the wait begins (wall clock) — they bound how long
// the user waits, not how long the search runs, so POST latency spends the
// same budget. The caller carries that start across remounts of the same
// search so a revisit doesn't hold the user again.

export type WaitPhase =
  | "holding"
  | "match"
  // Released to async: the search runs on, the user moves on.
  | "released"
  // Search finished with nothing to show.
  | "notFoundYet";

export type ReleaseReason = "cap" | "backlogged" | "offline" | null;

const isTerminal = (search: SearchResource | undefined): boolean =>
  !!search && (search.status === "complete" || search.status === "failed");

// The name floor a candidate must clear to be PRESENTED as a match (the server's
// resolver applies the same floor before it resolves a location). The
// surname-keyed sources (IL/NY/PA/OH) keep every similar surname regardless of
// first name, so a `matches` attempt can carry only wrong-first-name people:
// those stay in the data for review but never earn the "Is this X?" card.
const NAME_MATCH_FLOOR = 0.85;
const nameMatches = (c: Candidate): boolean =>
  c.nameExact === true || (typeof c.nameScore === "number" && c.nameScore >= NAME_MATCH_FLOOR);

// Applied once, at the data boundary: every consumer (the card, the records
// rows, the metrics) sees a `matches` attempt with only its floor-clearing
// candidates, so an attempt whose people all miss the floor reads as empty.
const withNameFloor = (search: SearchResource | undefined): SearchResource | undefined => {
  if (!search) return search;
  return {
    ...search,
    attempts: search.attempts.map((attempt) =>
      attempt.status === "matches"
        ? { ...attempt, candidates: attempt.candidates.filter(nameMatches) }
        : attempt,
    ),
  };
};

const findMatchAttempt = (search: SearchResource | undefined): SearchAttempt | null => {
  if (!search) return null;
  for (const attempt of search.attempts) {
    if (attempt.status === "matches" && attempt.candidates.length > 0) {
      return attempt;
    }
  }
  return null;
};

export function useSearchPoll(
  searchId: number | null,
  startedAt?: number,
  tuning: WaitTuning = DEFAULT_WAIT_TUNING,
  // The reason this search already released, if it did — carried by the
  // caller across remounts (WaitMemory) so Back/forward lands straight on the
  // released surface even if the signal has since flapped back.
  releasedBefore: ReleaseReason = null,
) {
  // A revisit starts with whatever clocks already elapsed, so a settled
  // surface can't flash the searching one for a frame before the timer
  // effect runs.
  const alreadyElapsed = startedAt ? Math.max(0, Date.now() - startedAt) : 0;
  const [minWaitPassed, setMinWaitPassed] = useState(alreadyElapsed >= tuning.minWaitMs);
  const [capPassed, setCapPassed] = useState(alreadyElapsed >= tuning.inProgressCapMs);
  const [ceilingPassed, setCeilingPassed] = useState(alreadyElapsed >= tuning.pollCeilingMs);
  // The release is one-way for the SEARCH, not this mount, and first reason
  // wins: once the wait has released — by signal or by cap — neither a signal
  // flapping back to in_progress nor a late signal after a cap release can
  // change what happened. The ref is written during render deliberately:
  // idempotent, monotonic, derived from committed query data, so the release
  // lands in the same render as the poll that caused it.
  const releasedAs = useRef<ReleaseReason>(releasedBefore);

  useEffect(() => {
    const elapsed = startedAt ? Math.max(0, Date.now() - startedAt) : 0;
    const remaining = (ms: number) => Math.max(0, ms - elapsed);
    const minWait = setTimeout(() => setMinWaitPassed(true), remaining(tuning.minWaitMs));
    const cap = setTimeout(() => setCapPassed(true), remaining(tuning.inProgressCapMs));
    const ceiling = setTimeout(() => setCeilingPassed(true), remaining(tuning.pollCeilingMs));
    return () => {
      clearTimeout(minWait);
      clearTimeout(cap);
      clearTimeout(ceiling);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enabled = searchId !== null;
  const query = useQuery<SearchResource>({
    queryKey: ["search", searchId],
    queryFn: () => getSearch(searchId as number),
    enabled,
    refetchInterval: (q) => {
      if (ceilingPassed) return false;
      const latest = q.state.data;
      // A completed search row can still owe the queue's answer (an open vine
      // attempt keeps the contract's phase at "searching") — keep polling so
      // the runner's write-back can land in place.
      if (latest && latest.status === "complete" && progressFrom(latest).phase === "resolved") {
        return false;
      }
      const p = progressFrom(latest);
      const released =
        capPassed ||
        releasedAs.current !== null ||
        p.signal === "backlogged" ||
        p.signal === "offline";
      return released ? tuning.releasedPollMs : tuning.holdPollMs;
    },
  });

  const search = useMemo(() => withNameFloor(query.data), [query.data]);
  const matchAttempt = useMemo(() => findMatchAttempt(search), [search]);
  const progress = progressFrom(search);
  const signalSaysRelease = progress.signal === "backlogged" || progress.signal === "offline";
  // The search row alone is not the end of the story: it can settle while the
  // queue still owes an answer, so "done" defers to the contract's phase.
  const searchSettled = isTerminal(search) && progress.phase === "resolved";

  let phase: WaitPhase = "holding";
  if (minWaitPassed && matchAttempt) {
    phase = "match";
  } else if (minWaitPassed && searchSettled) {
    phase = "notFoundYet";
  } else if (minWaitPassed && (releasedAs.current || signalSaysRelease || capPassed)) {
    phase = "released";
  }
  if (phase === "released" && releasedAs.current === null) {
    releasedAs.current = signalSaysRelease
      ? progress.signal === "offline"
        ? "offline"
        : "backlogged"
      : "cap";
  }

  // releaseReason stays set when a late match upgrades the surface, so the
  // exit can still report that the user had been released first.
  return {
    phase,
    search,
    matchAttempt,
    releaseReason: releasedAs.current,
    minWaitPassed,
    searchDone: searchSettled,
  };
}
